import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "./section-components/AdminAuthContext";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Avatar,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Map as MapIcon,
  Assessment as AssessmentIcon,
  Apps as AppsIcon,
  ExitToApp as ExitToAppIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";

const AdminPending = () => {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const initials = admin?.initials || "A";
  const fullName = admin?.fullName || "Utilisateur non connecté";
  const role = admin?.role || "Non défini";
  const profileImage = admin?.profileImage || "https://via.placeholder.com/100";

  const [openSublist, setOpenSublist] = useState(null);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [expandedPropertyId, setExpandedPropertyId] = useState(null);

  const handleSublistToggle = (index) => {
    setOpenSublist((prev) => (prev === index ? null : index));
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const fetchPendingProperties = async () => {
    try {
      const response = await axios.get("https://keurgui-backend-kjour.ondigitalocean.app/pending");
      setPendingProperties(response.data);
    } catch (error) {
      console.error("Erreur lors de la récupération des propriétés en attente:", error);
    }
  };

  useEffect(() => {
    fetchPendingProperties();
  }, []);

  const toggleDetails = (id) => {
    setExpandedPropertyId(expandedPropertyId === id ? null : id);
  };

  const handleApprove = async (id) => {
    try {
      await axios.patch(`https://keurgui-backend-kjour.ondigitalocean.app/products/${id}/approve`);
      setPendingProperties((prev) => prev.filter((p) => p._id !== id));
    } catch (error) {
      console.error("Erreur lors de l'approbation :", error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette propriété ?")) return;
    try {
      await axios.delete(`https://keurgui-backend-kjour.ondigitalocean.app/api/products/${id}`);
      setPendingProperties((prev) => prev.filter((p) => p._id !== id));
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/loginadmin");
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: 240,
          "& .MuiDrawer-paper": { width: 240, bgcolor: "#003366", color: "white", position: "fixed" },
        }}
      >
        <Box sx={{ padding: 2, textAlign: "center" }}>
          <Typography variant="h6">KEURGUI</Typography>
          <img
            src={profileImage}
            alt={fullName}
            style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", margin: "16px auto" }}
            onError={(e) => (e.target.src = "https://via.placeholder.com/80")}
          />
          <Typography>{fullName}</Typography>
          <Typography variant="body2">{role}</Typography>
        </Box>
        <List>
          {[
            { label: "Tableau de bord", path: "/admin", icon: <HomeIcon sx={{ color: "green" }} /> },
            {
              label: "Propriété",
              icon: <ApartmentIcon sx={{ color: "yellow" }} />,
              subItems: [
                { label: "Propriétés a vendre", path: "/adminpv" },
                { label: "Propriétés a louer", path: "/adminpa" },
                { label: "Ajouter une propriété", path: "/ajoutp" },
                { label: "Propriétés en attente", path: "/pending" },
              ],
            },
            {
              label: "Types",
              icon: <CategoryIcon sx={{ color: "red" }} />,
              subItems: [
                { label: "Appartement", path: "/apt" },
                { label: "Bureau_commerce", path: "/bureau_commerce" },
                { label: "Hotel_restaurant", path: "/hotel_restaurant" },
                { label: "Immeuble", path: "/immeuble" },
                { label: "Residence", path: "/residence" },
                { label: "Studio_chambre", path: "/studio_chambre" },
                { label: "Villa_maison", path: "/villa_maison" },
                { label: "Terrain", path: "/terrain" },
              ],
            },
            { label: "Agents", icon: <PersonIcon sx={{ color: "teal" }} />, subItems: [
              { label: "Tous les agents", path: "/agnts" },
              { label: "Ajouter un agent", path: "/add-agent" },
            ] },
            { label: "Carte", path: "/carte", icon: <MapIcon sx={{ color: "blue" }} /> },
            { label: "Rapports", path: "/rapports", icon: <AssessmentIcon sx={{ color: "orange" }} /> },
            { label: "Application", path: "/application", icon: <AppsIcon sx={{ color: "salmon" }} /> },
          ].map((item, index) => (
            <React.Fragment key={item.label}>
              <ListItem button onClick={() => (item.subItems ? handleSublistToggle(index) : handleNavigation(item.path))}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
                {item.subItems ? (openSublist === index ? <ExpandLess /> : <ExpandMore />) : null}
              </ListItem>
              {item.subItems && (
                <Collapse in={openSublist === index} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem button sx={{ pl: 4 }} key={subItem.label} onClick={() => handleNavigation(subItem.path)}>
                        <ListItemText primary={subItem.label} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, bgcolor: "#f5f5f5", p: 1, ml: 0 }}>
        {/* Top Bar */}
        <AppBar position="static" sx={{ bgcolor: "#003366" }}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1, color: "white" }}>
              Propriétés en attente
            </Typography>
            <IconButton color="inherit" onClick={handleLogout}><ExitToAppIcon /></IconButton>
            <Avatar sx={{ bgcolor: "#FFA726", ml: 2 }}>{initials}</Avatar>
          </Toolbar>
        </AppBar>

        {/* Liste des propriétés */}
        {pendingProperties.length === 0 ? (
          <Typography sx={{ mt: 3 }}>Aucune propriété en attente.</Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {pendingProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} key={property._id}>
                <Card>
                  {property.images && property.images.length > 0 && (
                    <CardMedia component="img" height="180" image={property.images[0]} alt={property.title} />
                  )}
                  <CardContent>
                    <Typography variant="h6">{property.title}</Typography>
                    <Typography>{property.address}, {property.city}</Typography>
                    <Typography>Prix: {property.price?.toLocaleString()} FCfa</Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Button variant="contained" color="primary" onClick={() => toggleDetails(property._id)}>
                        {expandedPropertyId === property._id ? "Cacher détails" : "Détails"}
                      </Button>
                      <Button variant="contained" color="success" onClick={() => handleApprove(property._id)}>Approuver</Button>
                      <Button variant="contained" color="error" onClick={() => handleDelete(property._id)}>Supprimer</Button>
                    </Stack>

                    {/* Détails */}
                    <Collapse in={expandedPropertyId === property._id} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 2, borderTop: '1px solid #ccc', pt: 2 }}>
                        <Typography><strong>Type:</strong> {property.productType}</Typography>
                        <Typography><strong>Transaction:</strong> {property.transactionType}</Typography>
                        <Typography><strong>Catégorie:</strong> {property.propertyCategory}</Typography>
                        <Typography><strong>Status:</strong> {property.status}</Typography>
                        <Typography><strong>Codu:</strong> {property.coduStatus}</Typography>
                        <Typography><strong>Chambres:</strong> {property.features.bedrooms}</Typography>
                        <Typography><strong>Salles de bain:</strong> {property.features.bathrooms}</Typography>
                        <Typography><strong>Garage:</strong> {property.features.garages}</Typography>
                        <Typography><strong>Surface:</strong> {property.features.area} m²</Typography>
                        <Typography><strong>Piscine:</strong> {property.features.hasPool ? "Oui" : "Non"}</Typography>
                        <Typography><strong>Description:</strong> {property.description || "Aucune"}</Typography>

                        {/* Toutes les images */}
                        <Stack direction="row" spacing={1} sx={{ mt: 1, overflowX: 'auto' }}>
                          {property.images?.map((img, idx) => (
                            <CardMedia key={idx} component="img" image={img} alt={`img-${idx}`} sx={{ height: 100, width: 100, objectFit: 'cover', borderRadius: 1 }} />
                          ))}
                        </Stack>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default AdminPending;